import { injectable } from "inversify";
import { parse } from "sparkson";
import { VintomError } from "../../api/model/error/VintomError";
import { Config } from "../../config/config";
import { ConfigurationOverride } from "../../model/playerConfiguration/ConfigurationOverride";
import { FullPlayerConfiguration } from "../../model/playerConfiguration/FullPlayerConfiguration";
import { PlayerConfiguration } from "../../model/playerConfiguration/PlayerConfiguration";

export interface ConfigurationBuilder {
  build(videoCode: string, configurationJSON: any, configurationOverrideJSON: any, personalizationJSON: any): Promise<FullPlayerConfiguration>;
}

@injectable()
export class ConfigurationBuilderImpl implements ConfigurationBuilder {

  build(videoCode: string, configurationJSON: any, configurationOverrideJSON: any, personalizationJSON: any): Promise<FullPlayerConfiguration> {
    if (videoCode) {
      const playerServerUrl = Config.videoConfigurationUrl(
          (configurationOverrideJSON ? configurationOverrideJSON.playerServerUrl : null),
          videoCode);
      return this.fetchConfig(playerServerUrl)
        .then((fetchedConfigurationJSON) => this.parseAndMerge(fetchedConfigurationJSON, configurationOverrideJSON, personalizationJSON));
    } else {
      return this.parseAndMerge(configurationJSON, configurationOverrideJSON, personalizationJSON);
    }
  }

  private parseAndMerge(configurationJSON: any, configurationOverrideJSON: any, personalizationJSON: any): Promise<FullPlayerConfiguration> {
    try {
      const conf = parse(PlayerConfiguration, configurationJSON);
      let confOverride: ConfigurationOverride;
      if (configurationOverrideJSON) {
        confOverride = parse(ConfigurationOverride, configurationOverrideJSON);
      }
      return Promise.resolve(new FullPlayerConfiguration(
        conf.code,
        conf.scenarioElements,
        confOverride && confOverride.analyticsEndpoints || conf.analyticsEndpoints,
        conf.aspectRatio,
        conf.parts,
        conf.videoDate,
        confOverride && confOverride.playerControls || conf.playerControls,
        conf.status,
        confOverride && confOverride.playerServerUrl || Config.defaultPlayerServerUrl,
        confOverride && confOverride.ctaStorageUrl || Config.defaultCTAStorageUrl,
        this.calculateVideoJSUrl(confOverride && confOverride.jsAssetsUrl || conf.jsAssetsUrl || Config.defaultJSAssetsLocation),
        this.calculateLottieUrl(confOverride && confOverride.jsAssetsUrl || conf.jsAssetsUrl || Config.defaultJSAssetsLocation),
        confOverride && confOverride.adminMode || conf.adminMode,
        confOverride && confOverride.autoplay || conf.autoplay,
        confOverride && confOverride.mutedStart || conf.mutedStart,
        confOverride && confOverride.autoRefreshBeforeReady || conf.autoRefreshBeforeReady,
        confOverride && confOverride.subtitlesActiveOnStart || conf.subtitlesActiveOnStart,
        personalizationJSON,
        conf.campaign,
        confOverride && confOverride.skin || conf.skin,
        conf.groupId
      ));
    } catch (e) {
      return Promise.reject(new VintomError("configurationParsingFailure", "Failed to parse configuration", e));
    }
  }

  private fetchConfig(url: string): Promise<any> {
    return fetch(url)
      .then((response) => response.json())
      .catch(() => Promise.reject(new VintomError("resourceLoadingFailure", `Failed to load configuration from '${url}'`)));
  }

  private calculateVideoJSUrl(jsAssetsUrl: string): string {
    return `${jsAssetsUrl}${Config.defaultVideoJSLocation}`;
  }

  private calculateLottieUrl(jsAssetsUrl: string): string {
    return `${jsAssetsUrl}${Config.defaultLottieJSLocation}`;
  }
}
